import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import DashboardLayout from "@/components/layout/DashboardLayout";
import ProgressMessages from "@/components/ProgressMessages";
import PostAnalyzer from "@/components/PostAnalyzer";
import { 
  Sparkles, 
  Zap, 
  Hash, 
  Clock, 
  TrendingUp,
  RefreshCw,
  Check,
  X,
  ArrowUp,
  ArrowDown,
  Lock,
  ArrowRight,
  ArrowLeft,
  FileText,
  Video,
  Image,
  Globe,
  Layers,
  Calendar,
  Eye,
  Loader2,
  Link2,
  Search
} from "lucide-react";

// Trending hashtags database (would come from backend in production)
const trendingHashtags = {
  general: ["#viral", "#trending", "#fyp", "#foryou", "#explore", "#instagood", "#love", "#photooftheday"],
  business: ["#entrepreneur", "#business", "#success", "#motivation", "#marketing", "#smallbusiness"],
  lifestyle: ["#lifestyle", "#fashion", "#beauty", "#travel", "#food", "#fitness", "#health"],
  tech: ["#tech", "#ai", "#innovation", "#startup", "#coding", "#developer", "#technology"]
};

// Words that boost viral potential
const viralBoosters = ["exclusive", "limited", "free", "secret", "amazing", "incredible", "breaking", "urgent", "new", "first"];

const platforms = [
  { id: "facebook", name: "Facebook", color: "#1877F2" },
  { id: "instagram", name: "Instagram", color: "#E4405F" },
  { id: "tiktok", name: "TikTok", color: "#000000" },
  { id: "pinterest", name: "Pinterest", color: "#BD081C" },
  { id: "linkedin", name: "LinkedIn", color: "#0A66C2" },
  { id: "youtube", name: "YouTube", color: "#FF0000" },
  { id: "twitter", name: "X (Twitter)", color: "#000000" }
];

export default function ViralSimulatorPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser } = useAuth();
  
  // Check if content was passed from the create flow
  const passedContent = location.state?.content || location.state?.caption || null;
  const passedPostId = location.state?.postId || null;
  
  // Tab state: "simulator" or "analyzer"
  const [activeTab, setActiveTab] = useState(location.state?.tab || "simulator");
  
  // Step state: 0 = Simulator mode, 1 = Single/Bulk, 2 = Content Type, 3 = Platforms, 4 = Generating, 5 = Done
  const [step, setStep] = useState(0);
  
  // Simulator state
  const [caption, setCaption] = useState(passedContent || "");
  const [customHashtags, setCustomHashtags] = useState(
    location.state?.hashtags?.join(" ") || ""
  );
  const [platform, setPlatform] = useState(location.state?.platform || "instagram");
  const [postTime, setPostTime] = useState("optimal");
  const [viralScore, setViralScore] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const [hashtagScores, setHashtagScores] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Creation flow state
  const [scheduleType, setScheduleType] = useState(null);
  const [contentType, setContentType] = useState("post");
  const [selectedPlatforms, setSelectedPlatforms] = useState(["facebook", "instagram", "tiktok", "pinterest", "linkedin", "youtube", "twitter"]);
  const [days, setDays] = useState(7);
  const [postsPerDay, setPostsPerDay] = useState(1);
  const [progress, setProgress] = useState(0);
  const [generatedPosts, setGeneratedPosts] = useState([]);
  
  // Credits
  const postsRemaining = (user?.posts_remaining || 0) + (user?.payg_posts_remaining || 0);
  const videosRemaining = user?.video_credits_remaining || 0;
  const planValidityDays = user?.plan_validity_days || 90;
  const maxDays = Math.min(planValidityDays, 90);
  
  const isVideoContent = contentType === "video";
  // For both post and video: days * postsPerDay * platforms for bulk, or platforms for single
  const creditsNeeded = scheduleType === "bulk" 
    ? days * postsPerDay * selectedPlatforms.length
    : selectedPlatforms.length;
  const creditsAvailable = isVideoContent ? videosRemaining : postsRemaining;
  const hasEnoughCredits = creditsNeeded <= creditsAvailable;
  const creditType = isVideoContent ? "video" : "post";

  const togglePlatform = (platformId) => {
    setSelectedPlatforms(prev => {
      if (prev.includes(platformId)) {
        if (prev.length === 1) return prev;
        return prev.filter(p => p !== platformId);
      }
      return [...prev, platformId];
    });
  };

  // Calculate viral score based on various factors
  const calculateViralScore = () => {
    if (caption.length < 10 && customHashtags.length < 3) {
      setViralScore(0);
      setSuggestions([]);
      return;
    }
    
    let score = 35;
    const factors = [];

    // Caption length
    if (caption.length > 0) {
      if (caption.length >= 50 && caption.length <= 150) {
        score += 15;
        factors.push({ text: "Optimal caption length", impact: "+15", positive: true });
      } else if (caption.length >= 20 && caption.length < 50) {
        score += 5;
        factors.push({ text: "Caption could be longer", impact: "+5", positive: true });
      } else if (caption.length < 20) {
        factors.push({ text: "Caption too short", impact: "0", positive: false });
      } else if (caption.length > 300) {
        score -= 5;
        factors.push({ text: "Caption might be too long", impact: "-5", positive: false });
      }
    }

    // Check for viral booster words
    const captionLower = caption.toLowerCase();
    const foundBoosters = viralBoosters.filter(word => captionLower.includes(word));
    if (foundBoosters.length > 0) {
      const boost = Math.min(foundBoosters.length * 5, 15);
      score += boost;
      factors.push({ text: `Power words: ${foundBoosters.join(", ")}`, impact: `+${boost}`, positive: true });
    }

    // Check for emojis
    const emojiCount = (caption.match(/[\u{1F300}-\u{1F9FF}]/gu) || []).length;
    if (emojiCount >= 1 && emojiCount <= 5) {
      score += 8;
      factors.push({ text: "Good emoji usage", impact: "+8", positive: true });
    } else if (emojiCount > 5) {
      score -= 3;
      factors.push({ text: "Too many emojis", impact: "-3", positive: false });
    }

    // Hashtag analysis
    const hashtags = customHashtags.split(/[\s,]+/).filter(h => h.startsWith("#") || h.length > 0);
    const formattedHashtags = hashtags.map(h => h.startsWith("#") ? h : `#${h}`);
    
    if (formattedHashtags.length >= 3 && formattedHashtags.length <= 10) {
      score += 10;
      factors.push({ text: "Good hashtag count (3-10)", impact: "+10", positive: true });
    } else if (formattedHashtags.length > 15) {
      score -= 5;
      factors.push({ text: "Too many hashtags", impact: "-5", positive: false });
    }

    // Check if using trending hashtags
    const allTrending = Object.values(trendingHashtags).flat();
    const trendingUsed = formattedHashtags.filter(h => allTrending.includes(h.toLowerCase()));
    if (trendingUsed.length > 0) {
      const boost = Math.min(trendingUsed.length * 3, 12);
      score += boost;
      factors.push({ text: `Trending hashtags used`, impact: `+${boost}`, positive: true });
    }

    // Platform-specific adjustments
    if (platform === "tiktok" && caption.includes("#fyp")) {
      score += 5;
      factors.push({ text: "TikTok FYP hashtag", impact: "+5", positive: true });
    }

    // Posting time
    if (postTime === "optimal") {
      score += 10;
      factors.push({ text: "Optimal posting time selected", impact: "+10", positive: true });
    }

    // Question or call-to-action
    if (caption.includes("?") || captionLower.includes("comment") || captionLower.includes("share")) {
      score += 8;
      factors.push({ text: "Engagement prompt detected", impact: "+8", positive: true });
    }

    // Ensure score is within bounds
    score = Math.max(0, Math.min(100, score));

    setViralScore(score);
    setSuggestions(factors);

    // Score the hashtags
    const scoredHashtags = formattedHashtags.map(tag => {
      const isTrending = allTrending.includes(tag.toLowerCase());
      return {
        tag,
        score: isTrending ? Math.floor(Math.random() * 20) + 80 : Math.floor(Math.random() * 40) + 40,
        trending: isTrending
      };
    }).sort((a, b) => b.score - a.score);
    
    setHashtagScores(scoredHashtags);
  };

  // Generate AI hashtag suggestions based on caption content
  const generateSuggestions = async () => {
    if (caption.length < 10) {
      toast.error("Please write at least 10 characters in your caption first");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await apiClient.post("/content/suggest-hashtags", {
        caption: caption,
        platform: platform,
        count: 5
      });
      
      if (response.data.hashtags && response.data.hashtags.length > 0) {
        const current = customHashtags.trim();
        const existingTags = current.toLowerCase().split(/\s+/);
        const newHashtags = response.data.hashtags.filter(
          t => !existingTags.includes(t.toLowerCase())
        );
        
        if (newHashtags.length > 0) {
          setCustomHashtags(current + (current ? " " : "") + newHashtags.join(" "));
          toast.success(`Added ${newHashtags.length} AI-suggested hashtags!`);
        } else {
          toast.info("All suggested hashtags are already added");
        }
      }
    } catch (error) {
      console.error("Hashtag suggestion error:", error);
      toast.error("Failed to generate hashtags");
    } finally {
      setLoading(false);
    }
  };

  // Generate AI caption suggestion
  const generateCaptionSuggestion = async () => {
    if (caption.length < 10) {
      toast.error("Please write at least 10 characters first");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await apiClient.post("/content/improve-caption", {
        caption: caption,
        platform: platform
      });
      
      if (response.data.improved_caption) {
        setCaption(response.data.improved_caption);
        toast.success("Caption improved with AI!");
      }
    } catch (error) {
      const improvements = [
        "Amazing content alert! ",
        "Check this out! ",
        "Can't believe this! ",
        " - What do you think?",
        " - Share your thoughts below!",
        " - Tag someone who needs to see this!"
      ];
      const prefix = improvements[Math.floor(Math.random() * 3)];
      const suffix = improvements[3 + Math.floor(Math.random() * 3)];
      
      const improved = prefix + caption + suffix;
      setCaption(improved);
      toast.success("Caption improved!");
    }
    
    setLoading(false);
  };

  useEffect(() => {
    if (caption || customHashtags) {
      calculateViralScore();
    }
  }, [caption, customHashtags, platform, postTime]);

  const getScoreColor = (score) => {
    if (score >= 80) return "text-viraly-success";
    if (score >= 60) return "text-viraly-orange";
    if (score >= 40) return "text-yellow-500";
    return "text-viraly-error";
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return "High viral potential!";
    if (score >= 60) return "Good potential";
    if (score >= 40) return "Moderate potential";
    if (score > 0) return "Needs improvement";
    return "Start typing to see score";
  };

  // Start the creation flow
  const startCreationFlow = () => {
    if (caption.length < 10) {
      toast.error("Please write at least 10 characters in your content first");
      return;
    }
    setStep(1);
  };

  // Generate content
  const handleGenerate = async () => {
    if (!hasEnoughCredits) {
      const needed = creditsNeeded - creditsAvailable;
      toast.error(`Need ${needed} more ${creditType} credit${needed > 1 ? 's' : ''}`, {
        action: {
          label: "Buy Credits",
          onClick: () => navigate(`/pricing?tab=payg&item=${creditType}s`)
        },
        duration: 5000
      });
      return;
    }

    setLoading(true);
    setStep(4);
    setProgress(0);

    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) return 90;
        return Math.min(90, prev + Math.floor(Math.random() * 8) + 3);
      });
    }, 500);

    try {
      const allPosts = [];
      const daysToGenerate = scheduleType === "bulk" ? days : 1;
      const postsPerDayCount = scheduleType === "bulk" ? postsPerDay : 1;
      const fullContent = caption + (customHashtags ? "\n\n" + customHashtags : "");

      for (let day = 0; day < daysToGenerate; day++) {
        for (let postNum = 0; postNum < postsPerDayCount; postNum++) {
          const scheduleDate = new Date();
          scheduleDate.setDate(scheduleDate.getDate() + day + 1);
          scheduleDate.setHours(10 + (postNum * 4), 0, 0, 0);

          if (contentType === "video") {
            // Generate actual video using RunwayML
            for (const platform of selectedPlatforms) {
              try {
                const videoRes = await apiClient.post("/content/video/generate", null, {
                  params: {
                    mode: "text",
                    prompt: `Create a ${platform} video about: ${fullContent}`,
                    duration: 10,
                    size: platform === "tiktok" ? "720x1280" : "1280x720"
                  }
                });
                
                // Create a post record for the video
                const postRes = await apiClient.post("/posts", {
                  platform,
                  caption: fullContent,
                  type: "video",
                  video_url: videoRes.data.url || null,
                  video_id: videoRes.data.video_id,
                  video_status: videoRes.data.status,
                  scheduled_at: scheduleDate.toISOString(),
                  status: "scheduled"
                });
                allPosts.push(postRes.data);
              } catch (err) {
                console.error("Video generation error:", err);
                toast.error(`Failed to generate video for ${platform}`);
              }
            }
          } else {
            // Generate regular posts
            const formPayload = new FormData();
            formPayload.append("content_type", "write");
            formPayload.append("platforms", selectedPlatforms.join(","));
            formPayload.append("text", `Create engaging social media content based on: "${fullContent}". Day ${day + 1}, variation ${postNum + 1}.`);

            const res = await apiClient.post("/content/generate-ai", formPayload, {
              headers: { "Content-Type": "multipart/form-data" }
            });

            for (const post of res.data.posts) {
              await apiClient.put(`/posts/${post.id}`, {
                scheduled_at: scheduleDate.toISOString(),
                status: "scheduled",
                type: contentType
              });
              allPosts.push(post);
            }
          }
        }
      }

      clearInterval(progressInterval);
      setProgress(100);
      setGeneratedPosts(allPosts);
      await refreshUser();
      setStep(5);
      toast.success(`Created ${allPosts.length} ${contentType === "video" ? "videos" : "posts"}!`);

    } catch (error) {
      clearInterval(progressInterval);
      console.error("Generation error:", error);
      toast.error("Failed to generate content");
      setStep(3);
    } finally {
      setLoading(false);
    }
  };

  // Step 0: Simulator Mode (default view)
  if (step === 0) {
    return (
      <DashboardLayout>
        <div className="p-6 md:p-8 max-w-6xl mx-auto">
          {/* Credits Banner */}
          <div className="mb-6 p-4 rounded-xl bg-white/5 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-viraly-orange" />
                <span className="text-sm">
                  <span className="text-viraly-orange font-bold">{postsRemaining}</span> post credits
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Video className="w-5 h-5 text-blue-400" />
                <span className="text-sm">
                  <span className="text-blue-400 font-bold">{videosRemaining}</span> video credits
                </span>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex items-center gap-2 mb-6 p-1 rounded-xl bg-white/5">
            <button
              onClick={() => setActiveTab("simulator")}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                activeTab === "simulator" 
                  ? "bg-viraly-gradient text-white" 
                  : "text-viraly-muted hover:text-white hover:bg-white/5"
              }`}
              data-testid="tab-simulator"
            >
              <Sparkles className="w-4 h-4" />
              Viral Simulator
            </button>
            <button
              onClick={() => setActiveTab("analyzer")}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                activeTab === "analyzer" 
                  ? "bg-viraly-gradient text-white" 
                  : "text-viraly-muted hover:text-white hover:bg-white/5"
              }`}
              data-testid="tab-analyzer"
            >
              <Search className="w-4 h-4" />
              Post Analyzer
            </button>
          </div>

          {/* Post Analyzer Tab */}
          {activeTab === "analyzer" && (
            <PostAnalyzer />
          )}

          {/* Viral Score Simulator Tab */}
          {activeTab === "simulator" && (
            <>
              <div className="mb-8">
                <h1 className="font-heading text-3xl font-bold mb-2 flex items-center gap-3">
                  <Sparkles className="w-8 h-8 text-viraly-orange" />
                  Viral Score Simulator
                </h1>
                <p className="text-viraly-muted">
                  Tweak and optimize in real-time, then create your post
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Section */}
            <div className="space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-base p-6"
              >
                <Label className="mb-3 block">Your Caption</Label>
                <Textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Write your post caption here... Try adding questions or calls-to-action!"
                  className="input-base min-h-[120px] mb-2"
                  data-testid="caption-input"
                />
                <p className="text-sm text-viraly-muted">
                  {caption.length} characters - Optimal: 50-150
                </p>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={generateCaptionSuggestion}
                  disabled={loading || caption.length < 10}
                  className="mt-2 text-viraly-orange hover:bg-viraly-orange/10"
                >
                  {loading ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  AI Improve Caption
                </Button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="card-base p-6"
              >
                <div className="flex items-center justify-between mb-3">
                  <Label>Hashtags</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={generateSuggestions}
                    disabled={loading}
                    className="text-viraly-orange hover:bg-viraly-orange/10"
                  >
                    {loading ? (
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    AI Suggest
                  </Button>
                </div>
                <Textarea
                  value={customHashtags}
                  onChange={(e) => setCustomHashtags(e.target.value)}
                  placeholder="#viral #trending #fyp"
                  className="input-base min-h-[80px]"
                  data-testid="hashtags-input"
                />
                
                {hashtagScores.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-sm font-medium">Hashtag Ranking:</p>
                    <div className="flex flex-wrap gap-2">
                      {hashtagScores.map((h, i) => (
                        <div
                          key={i}
                          className={`px-3 py-1 rounded-full text-sm flex items-center gap-1 ${
                            h.trending 
                              ? "bg-viraly-orange/20 text-viraly-orange" 
                              : "bg-white/5 text-viraly-muted"
                          }`}
                        >
                          {h.tag}
                          <span className={`text-xs ${h.score >= 70 ? "text-viraly-success" : ""}`}>
                            {h.score}
                          </span>
                          {h.trending && <TrendingUp className="w-3 h-3" />}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="card-base p-6"
              >
                {/* Create Content Button - no platform/time selection needed here, that happens during creation */}
                {caption.length >= 10 && (
                  <Button
                    onClick={startCreationFlow}
                    className="btn-primary w-full py-4"
                    data-testid="create-post-btn"
                  >
                    <ArrowRight className="w-5 h-5 mr-2" />
                    Create Post with This Content
                  </Button>
                )}
                
                {caption.length < 10 && (
                  <div className="text-center py-4 text-viraly-muted">
                    <p className="text-sm">Write at least 10 characters to create a post</p>
                  </div>
                )}
              </motion.div>
            </div>

            {/* Score Section */}
            <div className="space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="card-base p-8 text-center"
              >
                <p className="text-viraly-muted mb-2">Viral Score</p>
                <div className="relative w-48 h-48 mx-auto mb-4">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="96"
                      cy="96"
                      r="88"
                      fill="none"
                      stroke="rgba(255,255,255,0.1)"
                      strokeWidth="12"
                    />
                    <circle
                      cx="96"
                      cy="96"
                      r="88"
                      fill="none"
                      stroke="url(#gradient)"
                      strokeWidth="12"
                      strokeLinecap="round"
                      strokeDasharray={`${viralScore * 5.52} 552`}
                      className="transition-all duration-500"
                    />
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#FF6B35" />
                        <stop offset="100%" stopColor="#FF8C61" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`text-5xl font-heading font-bold ${getScoreColor(viralScore)}`}>
                      {viralScore}
                    </span>
                    <span className="text-viraly-muted">/100</span>
                  </div>
                </div>
                <p className={`text-lg font-medium ${getScoreColor(viralScore)}`}>
                  {getScoreLabel(viralScore)}
                </p>
              </motion.div>

              {/* Suggestions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="card-base p-6"
              >
                <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-viraly-orange" />
                  Score Breakdown
                </h3>
                
                {suggestions.length === 0 ? (
                  <p className="text-viraly-muted text-center py-4">
                    Start typing to see suggestions...
                  </p>
                ) : (
                  <div className="space-y-3">
                    {suggestions.map((suggestion, i) => (
                      <div
                        key={i}
                        className={`flex items-center justify-between p-3 rounded-xl ${
                          suggestion.positive ? "bg-viraly-success/10" : "bg-viraly-error/10"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {suggestion.positive ? (
                            <Check className="w-4 h-4 text-viraly-success" />
                          ) : (
                            <X className="w-4 h-4 text-viraly-error" />
                          )}
                          <span className="text-sm">{suggestion.text}</span>
                        </div>
                        <span className={`text-sm font-bold ${
                          suggestion.positive ? "text-viraly-success" : "text-viraly-error"
                        }`}>
                          {suggestion.impact}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>

              {/* Quick Tips */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="glass-card p-4"
              >
                <h4 className="font-medium mb-2 text-sm">Quick Tips:</h4>
                <ul className="text-sm text-viraly-muted space-y-1">
                  <li>Use 3-10 hashtags for best results</li>
                  <li>Include a question or call-to-action</li>
                  <li>Add 1-3 relevant emojis</li>
                  <li>Use power words like "exclusive" or "limited"</li>
                </ul>
              </motion.div>

              {/* Create Post with Optimized Content */}
              {viralScore >= 50 && caption.trim() && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="mt-4"
                >
                  <Button
                    onClick={() => {
                      // Combine optimized caption with hashtags
                      const fullContent = `${caption}${customHashtags ? `\n\n${customHashtags}` : ''}`;
                      navigate('/create', {
                        state: {
                          inputType: 'text',
                          prefilledText: fullContent,
                          fromViralSimulator: true,
                          viralScore: viralScore
                        }
                      });
                    }}
                    className="w-full bg-viraly-gradient hover:opacity-90 py-4 text-base font-semibold"
                    data-testid="create-post-from-simulator"
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    Create Post with This Content (Score: {viralScore})
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <p className="text-center text-xs text-viraly-muted mt-2">
                    Your optimized caption and hashtags will be pre-filled in the content creation flow
                  </p>
                </motion.div>
              )}
            </div>
          </div>
            </>
          )}
        </div>
      </DashboardLayout>
    );
  }

  // Creation Flow Steps
  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-3xl mx-auto">
        {/* Credits Banner */}
        <div className="mb-6 p-4 rounded-xl bg-white/5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-viraly-orange" />
              <span className="text-sm">
                <span className="text-viraly-orange font-bold">{postsRemaining}</span> post credits
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Video className="w-5 h-5 text-blue-400" />
              <span className="text-sm">
                <span className="text-blue-400 font-bold">{videosRemaining}</span> video credits
              </span>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-2 mb-6 p-1 rounded-xl bg-white/5">
          <button
            onClick={() => setActiveTab("simulator")}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
              activeTab === "simulator" 
                ? "bg-viraly-gradient text-white" 
                : "text-viraly-muted hover:text-white hover:bg-white/5"
            }`}
            data-testid="tab-simulator"
          >
            <Sparkles className="w-4 h-4" />
            Viral Simulator
          </button>
          <button
            onClick={() => setActiveTab("analyzer")}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
              activeTab === "analyzer" 
                ? "bg-viraly-gradient text-white" 
                : "text-viraly-muted hover:text-white hover:bg-white/5"
            }`}
            data-testid="tab-analyzer"
          >
            <Search className="w-4 h-4" />
            Post Analyzer
          </button>
        </div>

        {/* Post Analyzer Tab */}
        {activeTab === "analyzer" && (
          <PostAnalyzer />
        )}

        {/* Viral Simulator Tab */}
        {activeTab === "simulator" && (
          <>
            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-2xl bg-viraly-gradient flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <h1 className="font-heading text-3xl font-bold">Create from Simulator</h1>
              </div>
              <p className="text-viraly-muted">Turn your optimized content into scheduled posts</p>
            </div>

            {/* Progress Steps */}
            {step < 4 && (
              <div className="flex items-center justify-center gap-2 mb-8">
                {[1, 2, 3].map((s) => (
                  <div key={s} className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      step >= s ? "bg-viraly-gradient text-white" : "bg-white/10 text-viraly-muted"
                    }`}>
                      {step > s ? <Check className="w-4 h-4" /> : s}
                    </div>
                    {s < 3 && <div className={`w-8 h-0.5 mx-1 ${step > s ? "bg-viraly-orange" : "bg-white/10"}`} />}
                  </div>
                ))}
              </div>
            )}

        {/* STEP 1: Content Type (FIRST) */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button onClick={() => setStep(0)} className="flex items-center gap-2 text-viraly-muted hover:text-white mb-6">
              <ArrowLeft className="w-4 h-4" /> Back to Simulator
            </button>

            <div className="text-center mb-8">
              <p className="text-viraly-muted">What type of content do you want to create?</p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <button
                onClick={() => { setContentType("post"); setStep(2); }}
                className="p-6 rounded-xl border-2 border-white/10 hover:border-viraly-orange/50 bg-white/5 transition-all text-center group"
                data-testid="content-type-post"
              >
                <div className="w-16 h-16 rounded-xl bg-viraly-orange/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <Image className="w-8 h-8 text-viraly-orange" />
                </div>
                <h3 className="font-bold text-lg mb-1">Image / Text <span className="text-viraly-orange">Post</span></h3>
                <p className="text-sm text-viraly-muted">Images & captions</p>
                <div className="mt-3 flex items-center justify-center gap-2">
                  <span className="text-viraly-orange font-bold">{postsRemaining}</span>
                  <span className="text-xs text-viraly-muted">post credits</span>
                </div>
              </button>

              <button
                onClick={() => { setContentType("video"); setStep(2); }}
                className="p-6 rounded-xl border-2 border-white/10 hover:border-blue-500/50 bg-white/5 transition-all text-center group"
                data-testid="content-type-video"
              >
                <div className="w-16 h-16 rounded-xl bg-blue-500/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <Video className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="font-bold text-lg mb-1">Video <span className="text-blue-400">Post</span></h3>
                <p className="text-sm text-viraly-muted">Video concepts & scripts</p>
                <div className="mt-3 flex items-center justify-center gap-2">
                  <span className="text-blue-400 font-bold">{videosRemaining}</span>
                  <span className="text-xs text-viraly-muted">video credits</span>
                </div>
              </button>
            </div>
          </motion.div>
        )}

        {/* STEP 2: Single or Bulk (SECOND) */}
        {step === 2 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button onClick={() => setStep(1)} className="flex items-center gap-2 text-viraly-muted hover:text-white mb-6">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>

            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl font-bold mb-2">How Many?</h2>
              <p className="text-viraly-muted">Create one {contentType === "video" ? "video" : "post"} or schedule many?</p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <button
                onClick={() => { setScheduleType("single"); setStep(3); }}
                className="card-base p-6 hover:border-viraly-orange/50 transition-all text-left group"
                data-testid="single-post-btn"
              >
                <div className="w-12 h-12 rounded-xl bg-viraly-orange/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <FileText className="w-6 h-6 text-viraly-orange" />
                </div>
                <h3 className="font-bold mb-1">Single {contentType === "video" ? "Video" : "Post"}</h3>
                <p className="text-sm text-viraly-muted">Create one per platform</p>
              </button>

              <button
                onClick={() => { setScheduleType("bulk"); setStep(3); }}
                className="card-base p-6 hover:border-viraly-orange/50 transition-all text-left group"
                data-testid="bulk-schedule-btn"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Layers className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="font-bold mb-1">Bulk Schedule</h3>
                <p className="text-sm text-viraly-muted">Multiple variations over days</p>
              </button>
            </div>
          </motion.div>
        )}

        {/* STEP 3: Platforms */}
        {step === 3 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button onClick={() => setStep(2)} className="flex items-center gap-2 text-viraly-muted hover:text-white mb-6">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>

            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl font-bold mb-2">Select Platforms</h2>
              <p className="text-viraly-muted">Where do you want to post?</p>
            </div>

            <div className="flex gap-3 mb-6">
              {platforms.map((p) => (
                <button
                  key={p.id}
                  onClick={() => togglePlatform(p.id)}
                  className={`flex-1 p-4 rounded-xl flex flex-col items-center gap-2 transition-all border-2 ${
                    selectedPlatforms.includes(p.id)
                      ? "border-viraly-orange bg-viraly-orange/10"
                      : "border-white/10 bg-white/5 hover:border-white/20"
                  }`}
                  data-testid={`platform-${p.id}`}
                >
                  <Globe className="w-6 h-6" style={{ color: selectedPlatforms.includes(p.id) ? p.color : "#888" }} />
                  <span className="font-medium">{p.name}</span>
                  {selectedPlatforms.includes(p.id) && <Check className="w-4 h-4 text-viraly-orange" />}
                </button>
              ))}
            </div>

            {/* Bulk options */}
            {scheduleType === "bulk" && (
              <div className="mb-6 p-4 rounded-xl bg-white/5 space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-sm font-medium">Duration</label>
                    <span className="text-viraly-orange font-bold">{days} days</span>
                  </div>
                  <Slider value={[days]} onValueChange={(val) => setDays(val[0])} min={1} max={maxDays} step={1} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Posts per day per platform</label>
                  <div className="flex gap-2">
                    {[1, 2, 3].map((n) => (
                      <button
                        key={n}
                        onClick={() => setPostsPerDay(n)}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                          postsPerDay === n ? "bg-viraly-orange text-white" : "bg-white/10 hover:bg-white/20"
                        }`}
                      >
                        {n}x
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Credit Warning */}
            <div className={`mb-6 p-4 rounded-xl border ${
              isVideoContent ? "bg-blue-500/10 border-blue-500/20" : "bg-viraly-orange/10 border-viraly-orange/20"
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isVideoContent ? (
                    <Video className="w-5 h-5 text-blue-400" />
                  ) : (
                    <FileText className="w-5 h-5 text-viraly-orange" />
                  )}
                  <div>
                    <p className="text-sm font-medium">This will use {creditsNeeded} {creditType} credit{creditsNeeded !== 1 ? 's' : ''}</p>
                    <p className="text-xs text-viraly-muted">
                      {scheduleType === "bulk" 
                        ? `${days} days x ${postsPerDay}/day x ${selectedPlatforms.length} platforms`
                        : `${selectedPlatforms.length} platform(s) selected`}
                    </p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  hasEnoughCredits ? 'bg-viraly-success/20 text-viraly-success' : 'bg-red-500/20 text-red-400'
                }`}>
                  {creditsAvailable} {creditType} credits
                </span>
              </div>
            </div>

            <Button
              onClick={handleGenerate}
              disabled={selectedPlatforms.length === 0 || !hasEnoughCredits || loading}
              className="btn-primary w-full py-4"
              data-testid="generate-btn"
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Generating...</>
              ) : (
                <><Sparkles className="w-5 h-5 mr-2" /> Generate ({creditsNeeded} {creditType} credit{creditsNeeded !== 1 ? 's' : ''})</>
              )}
            </Button>
          </motion.div>
        )}

        {/* STEP 4: Generating */}
        {step === 4 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12">
            <div className="w-20 h-20 rounded-full bg-viraly-gradient flex items-center justify-center mx-auto mb-6 animate-pulse">
              {contentType === "video" ? (
                <Video className="w-10 h-10 text-white" />
              ) : (
                <Sparkles className="w-10 h-10 text-white" />
              )}
            </div>
            <h2 className="font-heading text-2xl font-bold mb-2">
              Creating Your {contentType === "video" ? "Videos" : "Content"}
            </h2>
            <p className="text-viraly-muted mb-6">
              {contentType === "video" 
                ? "Generating videos using AI... This may take a few minutes."
                : "Generating posts from your optimized content..."}
            </p>
            <div className="w-full max-w-xs mx-auto bg-white/10 rounded-full h-3 mb-2">
              <div className="bg-viraly-gradient h-3 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
            <p className="text-sm text-viraly-orange">{Math.round(progress)}%</p>
            <ProgressMessages progress={progress} />
          </motion.div>
        )}

        {/* STEP 5: Done */}
        {step === 5 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-viraly-success/20 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-viraly-success" />
              </div>
              <h2 className="font-heading text-2xl font-bold mb-2">
                {contentType === "video" ? "Videos" : "Content"} Created!
              </h2>
              <p className="text-viraly-muted">
                {generatedPosts.length} {contentType === "video" ? "videos" : "posts"} scheduled successfully
              </p>
            </div>

            {/* Preview */}
            <div className="space-y-3 mb-6 max-h-60 overflow-y-auto">
              {generatedPosts.slice(0, 3).map((post, i) => (
                <div key={i} className="card-base p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      post.platform === "facebook" ? "bg-blue-500/20" :
                      post.platform === "instagram" ? "bg-pink-500/20" : "bg-black/40"
                    }`}>
                      {contentType === "video" ? (
                        <Video className="w-5 h-5 text-blue-400" />
                      ) : (
                        <Globe className="w-5 h-5" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium capitalize">{post.platform}</p>
                      <p className="text-xs text-viraly-muted line-clamp-1">{post.caption?.substring(0, 60)}...</p>
                    </div>
                  </div>
                </div>
              ))}
              {generatedPosts.length > 3 && (
                <p className="text-center text-sm text-viraly-muted">+{generatedPosts.length - 3} more</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <Button 
                onClick={() => navigate(contentType === "video" ? "/posts?tab=videos" : "/posts?tab=scheduled")} 
                className="btn-primary flex items-center justify-center gap-2"
              >
                <Eye className="w-4 h-4" /> View {contentType === "video" ? "Videos" : "Posts"}
              </Button>
              <Button onClick={() => navigate("/calendar")} className="btn-secondary flex items-center justify-center gap-2">
                <Calendar className="w-4 h-4" /> View Calendar
              </Button>
            </div>

            <Button
              onClick={() => { setStep(0); setGeneratedPosts([]); }}
              className="btn-secondary w-full"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Back to Simulator
            </Button>
          </motion.div>
        )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
